import React, { createRef } from "react";
import styled from "styled-components";
import { useTranslation } from "@coworker/locales";
import FixaSearch from "@coworker/apprestructured/src/shared/wrappers/FixaSearch/FixaSearch";
import { LoaderIcon } from "@coworker/reusable/Loader";
import {
  Title,
  Container as BaseContainer,
  OverflowBackground,
} from "./CommonComponents";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import useAmountConfirmation from "./shared/useAmountConfirmation";
import useDuplicateTaskPopup from "./shared/useDuplicateTaskPopup";
import { useAvailableElevatedStock } from "../../hooks/useAvailableElevatedStock";
import { StockInfoPillsWithRelatedTasks } from "./StocknfoPillsWithRelatedTasks";
import { useTasksForProduct } from "../../hooks/useTasksForProduct";
import { TaskType } from "@coworker/apprestructured/src/tasks/enums/taskTypes";
import { useWorkspacesAction } from "../../hooks/useWorkspaces";
import tracker from "../../helpers/tracker";
import {
  LOCATION_TYPE_PICKUP,
  LOCATION_TYPE_REFILL,
} from "../../constants/locationWidgetTypes";
import {
  nonSLMFoodLocation,
  productQualityLocations,
} from "../../services/locations.service";
import amountTypeOptions from "@coworker/enums/amountTypes";
import { LocationGroup } from "./LocationPopup/LocationGroup";
import { locationTypes } from "../../constants/addOnLocationTypes";
import AddCustomLocationCTAButton from "./AddCustomLocationCTAButton";
import AddCustomLocation from "./LocationPopup/AddCustomLocation/AddCustomLocation";
import SlmSuggestionLocations from "./LocationPopup/AddCustomLocation/SlmSuggestionLocations";
import { useLocation } from "react-router";
import { useAllSalesLocationsForArticleQuery } from "../../hooks/Locations/useAllSalesLocationsForArticleQuery";
import { useRecentCustomLocations } from "../../hooks/Locations/useRecentCustomLocations";

function isOpenAddon(task: any) {
  return (
    task.task_type === TaskType.ADDON &&
    ["ASSIGNED", "UNASSIGNED"].includes(task.state)
  );
}

const Container = styled(BaseContainer)`
  height: 85%;
`;
const StockInfoContainer = styled("div")`
  margin: 12px 0 0 24px;
`;
const SearchContainer = styled("div")`
  margin: 24px;
`;

const LoaderContainer = styled("div")`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const MainText = styled("div")`
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 8px;
`;

const SubText = styled("div")`
  font-size: 14px;
`;

function EmptyLocationsList() {
  const { t } = useTranslation();
  return (
    <LoaderContainer>
      <MainText>{t("noLocationsAvailableString")}</MainText>
      <SubText>{t("noLocationsAvailableHelperString")}</SubText>
    </LoaderContainer>
  );
}

function DeterminePopupType(addon: any) {
  switch (addon) {
    case LOCATION_TYPE_PICKUP:
      return { title: "pickupLocation", isPickupLocationPopup: true };
    case LOCATION_TYPE_REFILL:
      return { title: "refillLocString", isPickupLocationPopup: false };
    default:
      return { title: "", isPickupLocationPopup: false };
  }
}

export function useGroupedFilteredLocations(
  fullId: string,
  query: string,
  isFoodArticle: boolean
) {
  const { data: salesLocations, isLoading: isLocationsLoading } =
    useAllSalesLocationsForArticleQuery(fullId);

  const hasNoSLMLocations = salesLocations?.length <= 0;

  const { sfmLocationList } = nonSLMFoodLocation(
    isFoodArticle,
    hasNoSLMLocations
  );

  const articleLocations =
    hasNoSLMLocations && isFoodArticle ? sfmLocationList : salesLocations;
  const loader = hasNoSLMLocations ? false : isLocationsLoading;

  return React.useMemo(() => {
    const keyword = query.toLowerCase();
    const mainLocations = [];
    const otherLocations = [];
    const sgfLocations = [];
    if (!loader) {
      for (const item of articleLocations) {
        const base = `${item.storeLocation || item.location} ${
          item.locationDescription || item.descriptive
        }`.toLocaleLowerCase(); // checking for both properties, since internal proxy and tasks service return different types
        if (!base.includes(keyword.toLocaleLowerCase())) continue;
        const {
          location,
          quantity,
          locationDescription, // type returned from internalProxy, can be removed later
          descriptive, // type returned by tasks service
          isPrimary,
          isHomeBase,
          isHome,
          isSGF,
        } = item;
        const frontendFriendlyObject = {
          code: location,
          quantity: hasNoSLMLocations ? "" : Number(quantity),
          section: locationDescription || descriptive,
          department: "",
          isPrimary,
          isHomeBase: isHome || isHomeBase,
        };
        if (isPrimary || isHomeBase || isHome) {
          mainLocations.push(frontendFriendlyObject);
        } else if (isSGF) {
          sgfLocations.push(frontendFriendlyObject);
        } else {
          otherLocations.push(frontendFriendlyObject);
        }
      }
    }
    return {
      mainLocations,
      otherLocations,
      sgfLocations,
      isLocationsLoading: loader,
    };
  }, [articleLocations, loader, query, hasNoSLMLocations]);
}

type LocationObject = {
  locationCode: string;
  locationSection: string;
  locationDepartment: string;
  locationQuantity: number;
  relatedOnThisLocation: number;
  locationCustom?: string;
  requestedAmount: number;
  actualRequestedAmount: number;
  requestedAmountType: typeof amountTypeOptions;
};

interface LocationProps {
  title: string;
  product_article_id: string;
  product_article_type: string;
  requestedAmount: number;
  requestedAmountType: typeof amountTypeOptions;
  onSubmit: (locationObject: LocationObject) => void;
  onClose: () => void;
  addon: any;
  locationCode: string;
  isFoodArticle: boolean;
  isProductQualityTask: boolean;
}

export default function Location({
  title,
  product_article_id = "",
  product_article_type = "",
  requestedAmount,
  requestedAmountType,
  onSubmit,
  onClose,
  addon,
  locationCode,
  isFoodArticle,
}: LocationProps) {
  const { t } = useTranslation();
  const { pop } = useWorkspacesAction();
  const amountConfirmation = useAmountConfirmation();
  const productFullId =
    !!product_article_type && !!product_article_id
      ? `${product_article_type}${product_article_id}`
      : "";
  const {
    available,
    elevated,
    loading: loadingStockInfo,
    color,
  } = useAvailableElevatedStock(productFullId);
  const { getDuplicateTaskConfirmation } = useDuplicateTaskPopup();

  // For other tasks (e.g. product issue), requestedAmount is not sent in, so its type is undefined
  const isAddonTask = typeof requestedAmount === "number";

  const searchInputRef = createRef<HTMLInputElement>();

  const handleClick = () => {
    if (searchInputRef.current) {
      searchInputRef?.current?.focus();
    }
  };

  const [query, setQuery] = React.useState("");

  const taskTypeFromUrl = useLocation().pathname.split("/")[3];
  let taskType = TaskType.ADDON;
  if (taskTypeFromUrl === "addon") {
    taskType = TaskType.ADDON;
  } else if (taskTypeFromUrl === "product") {
    taskType = TaskType.PRODUCT_ISSUE;
  }
  const isProductUrl = taskType === TaskType.PRODUCT_ISSUE;
  const showCustomLocations = isFoodArticle || isProductUrl;

  const { loading: loadingCustomLocations, customLocations } =
    useRecentCustomLocations(
      showCustomLocations ? query : "",
      showCustomLocations ? product_article_id : "",
      taskType,
      false
    );

  const hasCustomLocations = customLocations?.length > 0;

  const customLocationsObjects = hasCustomLocations
    ? customLocations.map((customLocation: string) => {
        return {
          code: customLocation,
        };
      })
    : [];

  const { mainLocations, otherLocations, sgfLocations, isLocationsLoading } =
    useGroupedFilteredLocations(productFullId, query, isFoodArticle);

  const isProductQualityTask =
    useLocation().pathname.includes("product_quality");

  const { productQualityLocationsList } =
    productQualityLocations(isProductQualityTask);

  const locationGroupsList = [
    {
      locationSectionTitle: locationTypes.HOME_OR_PRIMARY,
      locationsList: mainLocations,
    },
    {
      locationSectionTitle: locationTypes.OTHER_LOCATION,
      locationsList: otherLocations,
    },
    {
      locationSectionTitle: locationTypes.PRODUCT_QUALITY_LOCATION,
      locationsList: productQualityLocationsList,
      isProductQualityTask: isProductQualityTask,
    },
    {
      locationSectionTitle: locationTypes.SGF_LOCATION,
      locationsList: sgfLocations,
    },
    {
      locationSectionTitle: locationTypes.CUSTOM_LOCATION,
      locationsList: customLocationsObjects,
      isCustom: true,
    },
  ].filter((item) => {
    if (item.isCustom && showCustomLocations) {
      return hasCustomLocations || loadingCustomLocations;
    }
    return Array.isArray(item.locationsList) && item.locationsList.length > 0;
  });
  const relatedTasks = useTasksForProduct(product_article_id ?? "");

  const relatedAddons = React.useMemo(() => {
    const relatedAddons: any = {};
    if (!relatedTasks?.list?.forEach) return relatedAddons;
    relatedTasks?.list?.forEach((task) => {
      if (isOpenAddon(task)) {
        const key = task.location_grid_code;
        const current = relatedAddons[key] || [];
        current.push(task);
        relatedAddons[key] = current;
      }
    });
    return relatedAddons;
  }, [relatedTasks]);

  const submitWithConfirmation = React.useCallback(
    async (locationObject: any) => {
      const amountObject = {} as {
        requestedAmount: number;
        actualRequestedAmount: number;
        requestedAmountType: typeof amountTypeOptions;
      };
      if (
        requestedAmount &&
        requestedAmount > locationObject?.locationQuantity
      ) {
        amountObject["requestedAmount"] = 0;
        amountObject["actualRequestedAmount"] = 0;
        amountObject["requestedAmountType"] = requestedAmountType;
      }
      const homeLocationObj = mainLocations?.find(
        (location: any) => location.isHomeBase
      );
      const primaryLocationObj = mainLocations?.find(
        (location: any) => location.isPrimary
      );

      const homeandPrimary = {
        homeLocation: homeLocationObj?.code as string,
        primaryLocation: primaryLocationObj?.code as string,
      };
      onSubmit({ ...locationObject, ...homeandPrimary, ...amountObject });

      const { relatedOnThisLocation, locationCode } = locationObject || {};
      if (isAddonTask && relatedOnThisLocation > 0) {
        tracker.taskEfficiency.trackWarnDuplicate();
        const response = await getDuplicateTaskConfirmation(
          relatedOnThisLocation,
          product_article_id,
          locationCode
        );
        return response === false ? pop() : true;
      }
    },
    // (Ignored main Locations)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      available,
      requestedAmount,
      amountConfirmation,
      onSubmit,
      isAddonTask,
      getDuplicateTaskConfirmation,
      product_article_id,
      pop,
      isFoodArticle,
    ]
  );

  if (isProductQualityTask) {
    locationGroupsList.sort((a, b) => {
      if (a.isProductQualityTask) return -1;
      if (b.isProductQualityTask) return -1;
      else return 1;
    });
  }
  const popupType = DeterminePopupType(addon);
  const isPickupLocationSFM = popupType.isPickupLocationPopup && isFoodArticle;
  return (
    <>
      <OverflowBackground
        onClick={() => {
          tracker.trackDismissModal(false);
          onClose();
        }}
      />
      <Container>
        {popupType.title && (
          <FixaModalHeader title={t(popupType.title)} closeBtnClick={onClose} />
        )}
        <StockInfoContainer>
          <StockInfoPillsWithRelatedTasks
            stockInfo={{
              available,
              elevated,
              color,
              loading: loadingStockInfo,
            }}
            relatedTasksCount={relatedTasks?.formattedCount}
            ofWhichClosed={relatedTasks?.ofWhichClosed}
            productFullId={productFullId}
            productId={product_article_id}
            navigateToTaskForm={false}
            onClose={onClose}
          />
        </StockInfoContainer>
        {title && <Title>{title}</Title>}
        <SearchContainer>
          <FixaSearch
            ref={searchInputRef}
            placeholder={t("enterLocation2String")}
            data-testid="gridCodeQuery"
            onChange={(e) => {
              setQuery(e.target.value.toLocaleUpperCase());
            }}
            onClear={() => {
              setQuery("");
            }}
            id="searchLocationPopup"
          />
        </SearchContainer>
        {query && showCustomLocations && (
          <AddCustomLocation
            submitWithConfirmation={submitWithConfirmation}
            query={query}
          ></AddCustomLocation>
        )}
        {showCustomLocations && !isLocationsLoading
          ? false
          : (isLocationsLoading || loadingCustomLocations) && (
              <LoaderContainer>
                <LoaderIcon />
              </LoaderContainer>
            )}
        {locationGroupsList.map((locationGroup) => {
          const isCustomLocationGroup = !!locationGroup.isCustom;

          return (
            <LocationGroup
              isPickupLocationAndSFM={isPickupLocationSFM}
              isProductQualityTask={locationGroup.isProductQualityTask}
              isCustomLocation={!!locationGroup.isCustom || showCustomLocations}
              key={locationGroup.locationSectionTitle}
              locationGroupTitle={locationGroup.locationSectionTitle}
              locationsList={locationGroup.locationsList}
              relatedAddons={relatedAddons}
              selectedLocationCode={locationCode}
              submitWithConfirmation={submitWithConfirmation}
              articleNo={product_article_id || ""}
              isRefillLocation={popupType.title === "refillLocString"}
              isLoadingCustomLocations={
                isCustomLocationGroup && loadingCustomLocations
              }
            />
          );
        })}
        {query.length > 1 && (
          <SlmSuggestionLocations
            submitWithConfirmation={submitWithConfirmation}
            query={query}
          ></SlmSuggestionLocations>
        )}
        {showCustomLocations && query.length < 1 && (
          <AddCustomLocationCTAButton onClick={handleClick} />
        )}
        {!isLocationsLoading &&
          !loadingCustomLocations &&
          locationGroupsList.length === 0 &&
          productFullId &&
          !query && <EmptyLocationsList />}
      </Container>
    </>
  );
}
